import React, { useState } from 'react'
import {
  IconButton,
  Icon,
  SignOutIcon,
  GearIcon,
  MegaphoneIcon,
  InfoCircleIcon,
  SegmentedToggle,
} from '@ui'
import { useFlags } from '../flags/useFlags.js'

const INSTANCE_STUB = import.meta.env.VITE_INSTANCE_STUB || 'dev'

const INSTANCE_ITEMS = [
  { id: 'orgs', label: 'Orgs', path: '/orgs' },
  { id: 'analytics', label: 'Analytics', path: '/analytics' },
  { id: 'costs', label: 'Costs', path: '/costs' },
  { id: 'audit', label: 'Audit log', path: '/audit' },
  { id: 'notifications', label: 'Notifications', path: '/notifications' },
  { id: 'features', label: 'Features', path: '/features' },
]

const ORG_ITEMS_BASE = [
  { id: 'overview', label: 'Overview', path: '/org/overview' },
  { id: 'users', label: 'Users', path: '/org/users' },
  { id: 'org', label: 'Org structure', path: '/org/org' },
  { id: 'analytics', label: 'Analytics', path: '/org/analytics' },
  { id: 'settings', label: 'Settings', path: '/org/settings' },
]

function NavTabButton({ icon, label, active = false, onClick }) {
  const color = active ? '#3B6D11' : 'var(--text-secondary)'
  return (
    <button
      type="button"
      className="nav-tab"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      style={{ color, fontWeight: active ? 500 : 400 }}
    >
      <span style={tabStyles.iconWrap}>{icon}</span>
      <span style={tabStyles.label}>{label}</span>
    </button>
  )
}

const tabStyles = {
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
  },
  label: { fontSize: 10, lineHeight: 1.2 },
}

function NavSection({ items, currentPath, onNavigate }) {
  return (
    <div style={styles.section}>
      {items.map((item) => {
        const active = currentPath === item.path || currentPath.startsWith(item.path + '/')
        return (
          <button
            key={item.id}
            style={{ ...styles.navItem, ...(active ? styles.navItemActive : {}) }}
            onClick={() => onNavigate(item.path)}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

// Scope is URL-derived so the sidebar, pill, and nav items never drift from
// where the user actually is. Hybrid users still choose via the toggle below,
// but the toggle just navigates — it doesn't hold state.
export function deriveScope(currentPath) {
  return currentPath.startsWith('/org/') ? 'org' : 'super'
}

export function AdminSidebar({
  currentPath,
  onNavigate,
  onSignOut,
  onOpenFeedback,
  onScopeChange,
  me,
}) {
  const flags = useFlags()
  const settingsActive = currentPath === '/settings' || currentPath.startsWith('/settings/')
  const docsActive = currentPath.startsWith('/docs')
  const [collapsed, setCollapsed] = useState(false)

  const hasSuper = !!me?.isSuperAdmin
  const hasOrg = !!me?.isAdmin && !!me?.org
  const isHybrid = hasSuper && hasOrg

  const scope = deriveScope(currentPath)
  // Pick what to render. If only one role is available, force it regardless
  // of URL (so a pure org-admin on /settings still sees org nav + org pill).
  const effectiveScope = isHybrid ? scope : (hasSuper ? 'super' : 'org')

  const entraSsoEnabled = !!flags?.['entra-sso']?.enabled
  const orgItems = entraSsoEnabled
    ? [...ORG_ITEMS_BASE, { id: 'sso', label: 'SSO', path: '/org/sso' }]
    : ORG_ITEMS_BASE

  const items = effectiveScope === 'super' ? INSTANCE_ITEMS : orgItems
  const pillText = effectiveScope === 'super' ? INSTANCE_STUB : me?.org

  if (collapsed) {
    return (
      <nav className="sidebar-scope" style={styles.collapsed} aria-label="Primary navigation">
        <IconButton ariaLabel="Expand sidebar" onClick={() => setCollapsed(false)}>
          <Icon name="chevron-right" />
        </IconButton>
      </nav>
    )
  }

  return (
    <nav className="sidebar-scope" style={styles.sidebar} aria-label="Primary navigation">
      <div style={styles.brand}>
        <div style={styles.brandLeft}>
          <img src="/logo-mark.svg" alt="" width={26} height={26} />
          <div style={styles.brandText}>
            <span style={styles.brandNameRow}>
              <span style={styles.brandName}>Soundcheck</span>
              {pillText && (
                <span style={styles.orgPill} title="Admin portal scope">
                  {pillText}
                </span>
              )}
            </span>
            <span style={styles.brandTagline}>Prepare. Align. Grow.</span>
          </div>
        </div>
        <IconButton ariaLabel="Collapse sidebar" title="Collapse sidebar" onClick={() => setCollapsed(true)}>
          <Icon name="chevron-left" />
        </IconButton>
      </div>

      {isHybrid && onScopeChange && (
        <div style={styles.section} data-testid="scope-toggle">
          <SegmentedToggle
            ariaLabel="Switch admin scope"
            className="segmented--block"
            options={[
              { value: 'super', label: 'Super admin' },
              { value: 'org', label: 'Org admin' },
            ]}
            value={effectiveScope}
            onChange={onScopeChange}
          />
        </div>
      )}

      <div style={styles.scrollable}>
        <NavSection items={items} currentPath={currentPath} onNavigate={onNavigate} />
      </div>

      <div style={styles.footer}>
        <div style={styles.footerTabs}>
          <NavTabButton
            icon={<InfoCircleIcon />}
            label="About"
            active={docsActive}
            onClick={() => onNavigate('/docs')}
          />
          {onOpenFeedback && (
            <NavTabButton
              icon={<MegaphoneIcon />}
              label="Feedback"
              onClick={onOpenFeedback}
            />
          )}
          <NavTabButton
            icon={<GearIcon />}
            label="Settings"
            active={settingsActive}
            onClick={() => onNavigate('/settings')}
          />
          <NavTabButton
            icon={<SignOutIcon />}
            label="Sign out"
            onClick={onSignOut}
          />
        </div>
      </div>
    </nav>
  )
}

const styles = {
  sidebar: {
    width: '280px',
    minWidth: '280px',
    background: 'var(--sidebar-bg)',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  collapsed: {
    width: '32px',
    minWidth: '32px',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 4px',
    background: 'var(--sidebar-bg)',
  },
  brand: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '21px 21px 16px',
    borderBottom: '1px solid var(--border)',
  },
  brandLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
  brandName: { fontSize: '18.2px', fontWeight: 700, letterSpacing: '-0.02em' },
  brandNameRow: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  orgPill: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--danger-bg)',
    color: 'var(--danger-text)',
    lineHeight: 1.4,
  },
  brandTagline: { fontSize: '13px', color: 'var(--text-secondary)', marginTop: 3 },
  scrollable: { flex: 1, overflowY: 'auto', padding: '8px 0' },
  section: { padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navItem: {
    display: 'block',
    width: '100%',
    padding: '7px 10px',
    border: 'none',
    borderRadius: '4px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    textAlign: 'left',
    cursor: 'pointer',
  },
  navItemActive: {
    background: 'var(--border)',
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  footer: {
    borderTop: '0.5px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
  },
  footerTabs: {
    padding: '6px 0 8px',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
  },
}
